import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useRandevuTanimlar, randevuProjeleriGetir } from './useRandevuTanimlar';
import {
    RandevuDetay, RandevuVarsayilan, TEKRAR_TIPLERI, KAYNAK_TURLERI, tarihStr, saatStr, tarihOku, yerelIso, haritaUrl,
    hataMesaji, randevuDegisti,
} from './randevuOrtak';
import '../takip/takip.css';
import './randevu.css';

interface Props {
    mevcut?: RandevuDetay | null;
    varsayilan?: RandevuVarsayilan;
    onKapat: () => void;
    onKaydedildi?: (randevuID: number) => void;
}

interface KayitSecim { id: number; ad: string; telefon?: string | null; eposta?: string | null; }
type MusteriTipi = 'CARI' | 'LEAD' | 'DIGER';

const SURELER = [15, 30, 45, 60, 90, 120, 180, 240];
const HATIRLATMALAR = [
    { v: '', ad: 'Varsayılan' }, { v: '0', ad: 'Hatırlatma yok' }, { v: '15', ad: '15 dk önce' }, { v: '30', ad: '30 dk önce' },
    { v: '60', ad: '1 saat önce' }, { v: '120', ad: '2 saat önce' }, { v: '1440', ad: '1 gün önce' },
];

// Müşteri / potansiyel müşteri arama kutusu
const KayitAra: React.FC<{ tur: 'CARI' | 'LEAD'; secili: KayitSecim | null; onSec: (k: KayitSecim | null) => void }> = ({ tur, secili, onSec }) => {
    const [q, setQ] = useState('');
    const [sonuc, setSonuc] = useState<KayitSecim[]>([]);
    const [acik, setAcik] = useState(false);
    useEffect(() => {
        if (!acik) return;
        const z = window.setTimeout(() => {
            axiosInstance.get('/api/Randevular/kayit-ara', { params: { tur, q } }).then(r => setSonuc(r.data)).catch(() => setSonuc([]));
        }, 250);
        return () => window.clearTimeout(z);
    }, [q, tur, acik]);
    if (secili) return (
        <div className="tkp-girdi" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className={`fas ${tur === 'CARI' ? 'fa-user-tie' : 'fa-user-plus'}`} style={{ color: '#64748b' }}></i>
            <span style={{ flex: 1, fontWeight: 600 }}>{secili.ad}{secili.telefon ? <span style={{ color: '#94a3b8', fontWeight: 400 }}> · {secili.telefon}</span> : null}</span>
            <button type="button" className="tkp-ikon-btn" onClick={() => onSec(null)} title="Kaldır"><i className="fas fa-times"></i></button>
        </div>
    );
    return (
        <div style={{ position: 'relative' }}>
            <input className="tkp-girdi" value={q} onChange={e => setQ(e.target.value)} onFocus={() => setAcik(true)}
                onBlur={() => window.setTimeout(() => setAcik(false), 200)}
                placeholder={tur === 'CARI' ? 'Müşteri ara (ad, telefon)…' : 'Potansiyel müşteri ara (ad, telefon)…'} />
            {acik && sonuc.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.12)', zIndex: 5, maxHeight: 220, overflowY: 'auto' }}>
                    {sonuc.map(k => (
                        <div key={k.id} onMouseDown={() => { onSec(k); setQ(''); }} style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#fff7ed')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                            <b>{k.ad}</b>{k.telefon && <span style={{ color: '#94a3b8' }}> · {k.telefon}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const RandevuFormModal: React.FC<Props> = ({ mevcut, varsayilan, onKapat, onKaydedildi }) => {
    const { user } = useAuth();
    const ben = user?.kullaniciID ?? 0;
    const { tanimlar } = useRandevuTanimlar();
    const r = mevcut?.randevu;
    const ilkBas = r ? tarihOku(r.baslangic) : varsayilan?.baslangic ?? (() => { const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); return d; })();
    const ilkSure = r ? Math.round((tarihOku(r.bitis).getTime() - tarihOku(r.baslangic).getTime()) / 60000)
        : varsayilan?.bitis && varsayilan.baslangic ? Math.round((varsayilan.bitis.getTime() - varsayilan.baslangic.getTime()) / 60000) : 60;

    const [turID, setTurID] = useState<number | ''>(r?.turID ?? '');
    const [baslik, setBaslik] = useState(r?.baslik ?? '');
    const [baslikElle, setBaslikElle] = useState(!!r);
    const [tarih, setTarih] = useState(tarihStr(ilkBas));
    const [saat, setSaat] = useState(saatStr(ilkBas));
    const [sure, setSure] = useState(ilkSure);
    const [personelID, setPersonelID] = useState<number>(r?.personelID ?? varsayilan?.personelID ?? ben);
    const [musteriTipi, setMusteriTipi] = useState<MusteriTipi>(r?.leadID || varsayilan?.lead ? 'LEAD' : r && !r.cariID && (mevcut?.musteriAdi || mevcut?.musteriTelefon) ? 'DIGER' : 'CARI');
    const [cari, setCari] = useState<KayitSecim | null>(r?.cariID ? { id: r.cariID, ad: r.cariAdi ?? '' } : varsayilan?.cari ?? null);
    const [lead, setLead] = useState<KayitSecim | null>(r?.leadID ? { id: r.leadID, ad: r.leadAdi ?? '' } : varsayilan?.lead ?? null);
    const [musteriAdi, setMusteriAdi] = useState(mevcut?.musteriAdi ?? '');
    const [musteriTelefon, setMusteriTelefon] = useState(mevcut?.musteriTelefon ?? '');
    const [musteriEposta, setMusteriEposta] = useState(mevcut?.musteriEposta ?? '');
    const [projeID, setProjeID] = useState<number | ''>(r?.projeID ?? varsayilan?.projeID ?? '');
    const [kaynakID, setKaynakID] = useState<number | ''>(r?.kaynakID ?? '');
    const [konum, setKonum] = useState(r?.konum ?? '');
    const [aciklama, setAciklama] = useState(mevcut?.aciklama ?? '');
    const [hatirlatma, setHatirlatma] = useState(mevcut?.hatirlatmaDk != null ? String(mevcut.hatirlatmaDk) : '');
    const [musteriBildirim, setMusteriBildirim] = useState(mevcut ? mevcut.musteriBildirim : true);
    const [tekrarTipi, setTekrarTipi] = useState('');
    const [tekrarAdet, setTekrarAdet] = useState(4);
    const [takibeEkle, setTakibeEkle] = useState(false);
    const [kapsam, setKapsam] = useState<'tek' | 'sonrakiler'>('tek');
    const [projeler, setProjeler] = useState<{ projeID: number; projeAdi: string }[]>([]);
    const [oneriler, setOneriler] = useState<any[] | null>(null);
    const [kaydediliyor, setKaydediliyor] = useState(false);
    const [hata, setHata] = useState('');
    const [onay, setOnay] = useState<{ message: string; cakismalar: any[]; uyarilar: string[] } | null>(null);
    const baslikRef = useRef<HTMLInputElement>(null);

    useEffect(() => { randevuProjeleriGetir().then(setProjeler).catch(() => { }); }, []);
    useEffect(() => {
        const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onKapat(); };
        window.addEventListener('keydown', esc);
        return () => window.removeEventListener('keydown', esc);
    }, [onKapat]);

    const turler = (tanimlar?.turler ?? []).filter(t => t.aktifMi || t.turID === r?.turID);
    const kaynaklar = (tanimlar?.kaynaklar ?? []).filter(k => k.aktifMi || k.kaynakID === r?.kaynakID);
    const personel = (tanimlar?.personel ?? []).filter(p => p.randevuAlabilir || p.kullaniciID === personelID);
    const seciliTur = turler.find(t => t.turID === turID);
    const musteriAdGosterim = musteriTipi === 'CARI' ? cari?.ad : musteriTipi === 'LEAD' ? lead?.ad : musteriAdi;

    // Başlık kullanıcı yazmadıysa tür + müşteri adından oluşur
    useEffect(() => {
        if (baslikElle) return;
        const parca = [seciliTur?.ad, musteriAdGosterim].filter(Boolean).join(' — ');
        setBaslik(parca);
    }, [seciliTur?.ad, musteriAdGosterim, baslikElle]);

    const turSec = (id: number) => {
        setTurID(id);
        const t = turler.find(x => x.turID === id);
        if (t && !mevcut) setSure(t.varsayilanSure);
    };

    const baslangic = useMemo(() => new Date(`${tarih}T${saat || '09:00'}:00`), [tarih, saat]);
    const bitis = useMemo(() => new Date(baslangic.getTime() + sure * 60000), [baslangic, sure]);

    const uygunPersonelOner = async () => {
        setOneriler([]);
        try {
            const res = await axiosInstance.get('/api/Randevular/uygun-personel', {
                params: { baslangic: yerelIso(baslangic), bitis: yerelIso(bitis), projeID: projeID || undefined, haricRandevuID: r?.randevuID },
            });
            setOneriler(res.data);
        } catch (e) { setOneriler(null); setHata(hataMesaji(e)); }
    };

    const kaydet = async (cakismaOnay = false) => {
        if (!baslik.trim()) { setHata('Başlık girin.'); baslikRef.current?.focus(); return; }
        if (!tarih || !saat) { setHata('Tarih ve saat seçin.'); return; }
        setKaydediliyor(true); setHata('');
        const govde = {
            baslik: baslik.trim(),
            turID: turID || null,
            personelID,
            baslangic: yerelIso(baslangic),
            bitis: yerelIso(bitis),
            konum: konum.trim() || null,
            cariID: musteriTipi === 'CARI' ? cari?.id ?? null : null,
            leadID: musteriTipi === 'LEAD' ? lead?.id ?? null : null,
            projeID: projeID || null,
            kaynakID: kaynakID || null,
            musteriAdi: musteriTipi === 'DIGER' ? musteriAdi.trim() || null : null,
            musteriTelefon: musteriTipi === 'DIGER' ? musteriTelefon.trim() || null : null,
            musteriEposta: musteriTipi === 'DIGER' ? musteriEposta.trim() || null : null,
            aciklama: aciklama.trim() || null,
            musteriBildirim,
            hatirlatmaDk: hatirlatma === '' ? null : Number(hatirlatma),
            tekrarTipi: !mevcut && tekrarTipi ? tekrarTipi : null,
            tekrarAdet: !mevcut && tekrarTipi ? tekrarAdet : 0,
            cakismaOnay,
            takibeEkle: !mevcut && takibeEkle,
            siteUrl: window.location.origin,
        };
        try {
            const res = mevcut
                ? await axiosInstance.post(`/api/Randevular/update/${mevcut.randevu.randevuID}?kapsam=${kapsam}`, govde)
                : await axiosInstance.post('/api/Randevular', govde);
            if (res.data?.bildirimSonucu) window.setTimeout(() => alert(`Müşteri bildirimi: ${res.data.bildirimSonucu}`), 50);
            randevuDegisti();
            onKaydedildi?.(res.data?.randevular?.[0]?.randevuID ?? mevcut?.randevu.randevuID);
            onKapat();
        } catch (e: any) {
            const d = e?.response?.data;
            if (e?.response?.status === 409 && d?.kod === 'ONAY_GEREKLI') setOnay({ message: d.message, cakismalar: d.cakismalar ?? [], uyarilar: d.uyarilar ?? [] });
            else if (e?.response?.status === 409 && d?.kod === 'KAYNAK_DOLU')
                setHata(`${d.message} ${(d.cakismalar ?? []).map((c: any) => `${tarihOku(c.baslangic).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} ${c.baslik}`).join(', ')}`);
            else setHata(hataMesaji(e));
        } finally {
            setKaydediliyor(false);
        }
    };

    const musteriIletisimVar = musteriTipi === 'CARI' ? !!cari : musteriTipi === 'LEAD' ? !!lead : !!(musteriTelefon || musteriEposta);

    return (
        <div className="tkp-perde ust" onMouseDown={e => { if (e.target === e.currentTarget) onKapat(); }}>
            <div className="tkp-modal">
                <div className="tkp-modal-bas">
                    <i className="fas fa-calendar-plus" style={{ color: '#e67d21' }}></i>
                    <h5>{mevcut ? 'Randevuyu Düzenle' : 'Yeni Randevu'}</h5>
                    <button className="tkp-kapat" onClick={onKapat}>×</button>
                </div>
                <div className="tkp-modal-govde">
                    {hata && <div className="tkp-hata">{hata}</div>}
                    {onay && (
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, color: '#92400e' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}><i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>{onay.message}</div>
                            {onay.cakismalar.map((c, i) => (
                                <div key={i}>• {tarihOku(c.baslangic).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}–{saatStr(tarihOku(c.bitis))} {c.baslik}</div>
                            ))}
                            {onay.uyarilar.map((u, i) => <div key={`u${i}`}>• {u}</div>)}
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <button className="tkp-btn kucuk turuncu" onClick={() => { setOnay(null); kaydet(true); }}>Yine de kaydet</button>
                                <button className="tkp-btn kucuk" onClick={() => { setOnay(null); uygunPersonelOner(); }}>Uygun personel öner</button>
                                <button className="tkp-btn kucuk" onClick={() => setOnay(null)}>Saati değiştir</button>
                            </div>
                        </div>
                    )}

                    <div className="tkp-alan">
                        <label className="tkp-etiket">Randevu türü</label>
                        <div className="rnd-secim">
                            {turler.map(t => (
                                <button key={t.turID} type="button" className={turID === t.turID ? 'aktif' : ''} onClick={() => turSec(t.turID)}
                                    style={turID === t.turID ? { borderColor: t.renk, background: `${t.renk}14`, color: t.renk } : undefined}>
                                    <i className={`fas ${t.ikon || 'fa-calendar'}`} style={{ color: t.renk }}></i>{t.ad}
                                    <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{t.varsayilanSure} dk</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="tkp-alan">
                        <label className="tkp-etiket">Başlık *</label>
                        <input ref={baslikRef} className="tkp-girdi" value={baslik} maxLength={200} onChange={e => { setBaslik(e.target.value); setBaslikElle(true); }} placeholder="Ör. Ofis görüşmesi — Ahmet Yılmaz" />
                    </div>

                    <div className="tkp-satir">
                        <div className="tkp-alan">
                            <label className="tkp-etiket">Tarih *</label>
                            <input type="date" className="tkp-girdi" value={tarih} onChange={e => setTarih(e.target.value)} />
                        </div>
                        <div className="tkp-alan" style={{ maxWidth: 130 }}>
                            <label className="tkp-etiket">Saat *</label>
                            <input type="time" className="tkp-girdi" value={saat} step={300} onChange={e => setSaat(e.target.value)} />
                        </div>
                        <div className="tkp-alan" style={{ maxWidth: 170 }}>
                            <label className="tkp-etiket">Süre</label>
                            <select className="tkp-girdi" value={sure} onChange={e => setSure(Number(e.target.value))}>
                                {Array.from(new Set([...SURELER, sure])).sort((a, b) => a - b).map(s => (
                                    <option key={s} value={s}>{s < 60 ? `${s} dk` : `${Math.floor(s / 60)} sa${s % 60 ? ` ${s % 60} dk` : ''}`}</option>
                                ))}
                            </select>
                            <div className="tkp-ipucu">Bitiş: {saatStr(bitis)}{tarihStr(bitis) !== tarih ? ' (ertesi gün)' : ''}</div>
                        </div>
                    </div>

                    <div className="tkp-alan">
                        <label className="tkp-etiket" style={{ display: 'flex', alignItems: 'center' }}>
                            Personel
                            <button type="button" className="tkp-btn kucuk" style={{ marginLeft: 'auto', textTransform: 'none' }} onClick={uygunPersonelOner}>
                                <i className="fas fa-magic"></i>Uygun personel öner
                            </button>
                        </label>
                        <select className="tkp-girdi" value={personelID} onChange={e => setPersonelID(Number(e.target.value))}>
                            {personel.map(p => <option key={p.kullaniciID} value={p.kullaniciID}>{p.adSoyad}{p.ben ? ' (ben)' : ''}</option>)}
                        </select>
                        {oneriler && (
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 6, maxHeight: 180, overflowY: 'auto' }}>
                                {oneriler.length === 0 && <div className="tkp-bos" style={{ padding: 8 }}><i className="fas fa-spinner fa-spin"></i></div>}
                                {oneriler.map(o => (
                                    <div key={o.kullaniciID} onClick={() => { setPersonelID(o.kullaniciID); setOneriler(null); }}
                                        style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 10px', cursor: 'pointer', fontSize: 12.5, borderBottom: '1px solid #f1f5f9', opacity: o.uygun ? 1 : .6 }}>
                                        <i className={`fas ${o.uygun ? 'fa-check-circle' : 'fa-times-circle'}`} style={{ color: o.uygun ? '#16a34a' : '#dc2626' }}></i>
                                        <b style={{ minWidth: 140 }}>{o.adSoyad}</b>
                                        <span style={{ color: '#64748b', flex: 1 }}>{o.uygun ? `Müsait · o gün ${o.gunlukRandevu} randevu` : o.neden}</span>
                                        {o.projeErisimi && <span className="tkp-rozet yakin">Projede</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="tkp-alan">
                        <label className="tkp-etiket">Görüşülecek kişi</label>
                        <div className="rnd-grup" style={{ marginBottom: 6 }}>
                            {([['CARI', 'Müşteri'], ['LEAD', 'Potansiyel müşteri'], ['DIGER', 'Diğer']] as const).map(([v, ad]) => (
                                <button key={v} type="button" className={musteriTipi === v ? 'aktif' : ''} onClick={() => setMusteriTipi(v)}>{ad}</button>
                            ))}
                        </div>
                        {musteriTipi === 'CARI' && <KayitAra tur="CARI" secili={cari} onSec={setCari} />}
                        {musteriTipi === 'LEAD' && <KayitAra tur="LEAD" secili={lead} onSec={setLead} />}
                        {musteriTipi === 'DIGER' && (
                            <div className="tkp-satir">
                                <input className="tkp-girdi" value={musteriAdi} onChange={e => setMusteriAdi(e.target.value)} placeholder="Ad soyad" />
                                <input className="tkp-girdi" value={musteriTelefon} onChange={e => setMusteriTelefon(e.target.value)} placeholder="Telefon" />
                                <input className="tkp-girdi" value={musteriEposta} onChange={e => setMusteriEposta(e.target.value)} placeholder="E-posta" />
                            </div>
                        )}
                    </div>

                    <div className="tkp-satir">
                        <div className="tkp-alan">
                            <label className="tkp-etiket">Proje</label>
                            <select className="tkp-girdi" value={projeID} onChange={e => setProjeID(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">—</option>
                                {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
                            </select>
                        </div>
                        <div className="tkp-alan">
                            <label className="tkp-etiket">Kaynak (oda, numune daire, araç)</label>
                            <select className="tkp-girdi" value={kaynakID} onChange={e => setKaynakID(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">—</option>
                                {kaynaklar.filter(k => !k.projeID || !projeID || k.projeID === projeID).map(k => (
                                    <option key={k.kaynakID} value={k.kaynakID}>{k.ad} ({KAYNAK_TURLERI.find(x => x.t === k.tur)?.ad ?? k.tur})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="tkp-alan">
                        <label className="tkp-etiket">Konum</label>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <input className="tkp-girdi" value={konum} maxLength={500} onChange={e => setKonum(e.target.value)} placeholder="Adres, şantiye ya da online görüşme bağlantısı" />
                            {konum.trim() && !/^https?:/i.test(konum.trim()) && (
                                <a className="tkp-btn" href={haritaUrl(konum.trim())} target="_blank" rel="noreferrer" title="Haritada göster"><i className="fas fa-map-marker-alt"></i></a>
                            )}
                        </div>
                    </div>

                    <div className="tkp-alan">
                        <label className="tkp-etiket">Açıklama</label>
                        <textarea className="tkp-girdi" rows={3} value={aciklama} onChange={e => setAciklama(e.target.value)} placeholder="Görüşme konusu, hazırlanacaklar…" />
                    </div>

                    <div className="tkp-satir">
                        <div className="tkp-alan">
                            <label className="tkp-etiket">Personel hatırlatması</label>
                            <select className="tkp-girdi" value={hatirlatma} onChange={e => setHatirlatma(e.target.value)}>
                                {HATIRLATMALAR.map(h => <option key={h.v} value={h.v}>{h.ad}</option>)}
                            </select>
                        </div>
                        {!mevcut && (
                            <div className="tkp-alan">
                                <label className="tkp-etiket">Tekrar</label>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <select className="tkp-girdi" value={tekrarTipi} onChange={e => setTekrarTipi(e.target.value)}>
                                        {TEKRAR_TIPLERI.map(t => <option key={t.t} value={t.t}>{t.ad}</option>)}
                                    </select>
                                    {tekrarTipi && (
                                        <select className="tkp-girdi" value={tekrarAdet} onChange={e => setTekrarAdet(Number(e.target.value))} style={{ maxWidth: 110 }} title="Toplam randevu sayısı">
                                            {[2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 26, 52].map(n => <option key={n} value={n}>{n} kez</option>)}
                                        </select>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 6, opacity: musteriIletisimVar ? 1 : .55 }}>
                        <input type="checkbox" checked={musteriBildirim} onChange={e => setMusteriBildirim(e.target.checked)} style={{ accentColor: '#e67d21' }} />
                        Müşteriye SMS / e-posta ile bildir (onay ve hatırlatma, onay/iptal bağlantısıyla)
                    </label>
                    {!mevcut && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 6 }}>
                            <input type="checkbox" checked={takibeEkle} onChange={e => setTakibeEkle(e.target.checked)} style={{ accentColor: '#e67d21' }} />
                            Personelin Takip listesine de ekle
                        </label>
                    )}
                    {mevcut && mevcut.randevu.tekrarGrupID && (
                        <div className="tkp-alan" style={{ marginTop: 8 }}>
                            <label className="tkp-etiket">Tekrarlayan randevu</label>
                            <div className="rnd-grup">
                                <button type="button" className={kapsam === 'tek' ? 'aktif' : ''} onClick={() => setKapsam('tek')}>Yalnızca bu</button>
                                <button type="button" className={kapsam === 'sonrakiler' ? 'aktif' : ''} onClick={() => setKapsam('sonrakiler')}>Bu ve sonrakiler</button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="tkp-modal-alt">
                    <button className="tkp-btn" onClick={onKapat}>Vazgeç</button>
                    <button className="tkp-btn ana" onClick={() => kaydet(false)} disabled={kaydediliyor}>
                        <i className={`fas ${kaydediliyor ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>{mevcut ? 'Kaydet' : tekrarTipi ? `${tekrarAdet} Randevu Oluştur` : 'Randevu Oluştur'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RandevuFormModal;
